import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useParams } from 'react-router';

import { useGetRecipeQuery } from '@/api';
import Markdown from '@/modules/component/Markdown';
import { markdownBody } from '@/modules/component/Markdown/style';
import Page from '@/modules/component/Page';
import PageMeta from '@/modules/component/PageMeta';
import { registerLocaleResources, useCommonTranslation, useTranslation } from '@/modules/i18n/useTranslation';
import Error404Page from '@/modules/pages/404';

import en from './locales/en.json';
import es from './locales/es.json';
import ru from './locales/ru.json';

registerLocaleResources('CookBookDetailPage', { en, es, ru });

declare module '@/modules/i18n/namespaces' {
    interface LocaleNamespaces {
        CookBookDetailPage: typeof en;
    }
}

const CookBookDetailPage = () => {
    const { t } = useTranslation('CookBookDetailPage');
    const { t: tCommon } = useCommonTranslation();
    const { id = '' } = useParams<'id'>();
    const recipeId = /^\d+$/.test(id) ? Number(id) : undefined;
    // currentData, not data: while /cookbook/2 loads, the page must not show the previous recipe
    const { currentData: recipe, isError } = useGetRecipeQuery(recipeId ?? skipToken);

    if (recipeId === undefined) {
        return <Error404Page/>;
    }

    return (
        <Page>
            <PageMeta title={recipe?.title ?? t('title')}/>
            {recipe ? (
                <Typography component="div" align="left">
                    <h1>{recipe.title}</h1>
                    <Typography component="div" align="left" sx={markdownBody}>
                        <Markdown>{recipe.description}</Markdown>
                    </Typography>
                    <Typography component="div" align="right" color="text.secondary">
                        <Markdown>{recipe.resources ?? ''}</Markdown>
                    </Typography>
                </Typography>
            ) : isError ? (
                <Alert severity="error">{tCommon('error.unknown')}</Alert>
            ) : (
                <CircularProgress/>
            )}
        </Page>
    );
};

export default CookBookDetailPage;
